import React, { useEffect, useState } from "react";
import { Wrench, GearSix, Clock } from "@phosphor-icons/react";
import api from "@/lib/api";
import { devWarn } from "@/lib/debug";

// Full-screen maintenance/upgrade page shown to end users while the app is
// being deployed or migrated. Design goals:
//   • Load without any auth or backend call so it works even mid-deploy.
//   • Fetches optional custom message from `/api/public/maintenance` but
//     silently falls back to the default copy on any error.
//   • Auto-polls every 20s so the page redirects itself back to `/` once
//     the flag flips OFF — user doesn't have to refresh manually.
export default function Maintenance() {
  const [message, setMessage] = useState("");
  const [endAt, setEndAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  // Track whether we ever observed maintenance=true — only then does an
  // enabled=false response mean "deploy finished, go home". Without this
  // guard, admins previewing the page directly would get bounced home
  // on the very first poll.
  const wasEnabledRef = React.useRef(false);

  // Live poll — if maintenance is disabled while user waits, bounce them
  // back to root. Wrapped in try/catch so a 5xx during deploy doesn't
  // spam the console with red errors.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/public/maintenance");
        if (cancelled) return;
        if (data && data.message) setMessage(data.message);
        if (data && data.end_at !== undefined) setEndAt(data.end_at);
        if (data && data.enabled) {
          wasEnabledRef.current = true;
        } else if (data && data.enabled === false && wasEnabledRef.current) {
          // Deploy is done → send the user home.
          window.location.href = "/";
        }
      } catch (err) {
        // network error or backend still bootstrapping — keep waiting.
        // Dev-only surface so we can diagnose polling issues locally
        // without disturbing the end-user's upgrade experience.
        devWarn("[Maintenance] poll failed:", err);
      }
    };
    check();
    const t = setInterval(check, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Second interval — 1-second tick for the countdown display. Runs only
  // when we have an end_at so idle previews don't burn a timer needlessly.
  useEffect(() => {
    if (!endAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [endAt]);

  // Format countdown as "X menit Y detik" / "Y detik" / "0 detik" — no
  // hour bucket because a maintenance longer than that is unusual.
  const countdown = React.useMemo(() => {
    if (!endAt) return null;
    const end = new Date(endAt).getTime();
    if (!Number.isFinite(end)) return null;
    const diff = end - now;
    if (diff <= 0) return { text: "sebentar lagi", overdue: true };
    const totalSec = Math.floor(diff / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    if (m === 0) return { text: `${s} detik`, overdue: false };
    return { text: `${m} menit ${s} detik`, overdue: false };
  }, [endAt, now]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-neutral-900 text-white overflow-hidden"
      data-testid="maintenance-page"
    >
      {/* Grain / dot texture overlay for depth */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.7) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />
      {/* Floating orange accent glow */}
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #f97316, transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-15 blur-3xl"
        style={{ background: "radial-gradient(circle, #06b6d4, transparent 70%)" }}
      />

      <div className="relative z-10 max-w-lg w-full mx-auto px-6 text-center space-y-8">
        {/* Animated gear cluster */}
        <div className="relative h-32 flex items-center justify-center" data-testid="maintenance-icon-cluster">
          <GearSix
            size={96}
            weight="fill"
            className="text-orange-500 animate-spin"
            style={{ animationDuration: "6s" }}
          />
          <GearSix
            size={56}
            weight="fill"
            className="text-cyan-400 absolute -right-2 top-0 animate-spin"
            style={{ animationDuration: "4s", animationDirection: "reverse" }}
          />
          <Wrench
            size={40}
            weight="fill"
            className="text-white/80 absolute -left-4 bottom-2 -rotate-12"
          />
        </div>

        <div className="space-y-3">
          <div className="text-xs uppercase tracking-[0.4em] text-orange-400 font-bold">
            Sistem Sedang Di-Upgrade
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-none">
            Mohon Tunggu
            <br />
            <span className="text-orange-500">Sebentar</span>
          </h1>
          <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-md mx-auto">
            {message || (
              <>
                Aplikasi <strong className="text-white">epool</strong> sedang dalam
                proses pembaruan sistem. Halaman akan otomatis kembali normal setelah
                proses selesai.
              </>
            )}
          </p>
        </div>

        {/* Status pill with pulse */}
        <div
          className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 bg-white/5 backdrop-blur-sm rounded-full"
          data-testid="maintenance-status"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-orange-500" />
          </span>
          <span className="text-xs font-mono uppercase tracking-wider text-white/80">
            Update sedang berjalan
          </span>
        </div>

        {/* Countdown — only rendered when admin set end_at. Uses a
            monospace font to prevent width jumps on each second tick. */}
        {countdown && (
          <div
            className={`inline-flex items-center gap-3 px-6 py-3 border-2 backdrop-blur-sm mx-auto ${
              countdown.overdue
                ? "border-orange-400 bg-orange-500/10 text-orange-200"
                : "border-cyan-400/50 bg-cyan-500/10 text-cyan-100"
            }`}
            data-testid="maintenance-countdown"
          >
            <Clock size={22} weight="bold" />
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-widest opacity-70">
                Estimasi selesai
              </div>
              <div className="text-lg font-black font-mono tabular-nums" data-testid="maintenance-countdown-text">
                {countdown.overdue
                  ? "Menyelesaikan…"
                  : <>Selesai dalam <span className="text-cyan-300">~{countdown.text}</span></>}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-center gap-2 text-xs text-white/50 pt-4">
          <Clock size={14} />
          <span>Halaman ini refresh otomatis setiap 20 detik</span>
        </div>

        <div className="text-[10px] uppercase tracking-widest text-white/30 font-mono pt-6">
          PT. Sari Bakti Jaya · Cement Loading Control
        </div>
      </div>
    </div>
  );
}
